// Visualização compacta para o SECRETÁRIO solicitante.
// Não edita a demanda — só acompanha status, responde pedidos de info,
// cadastra parceiros (com link público) e conversa com a Comunicação.
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Send, Users, Link2, CheckCircle2, Clock, AlertCircle, MessageSquare, HelpCircle, History,
  Image as ImageIcon,
} from 'lucide-react';
import { format } from 'date-fns';
import { api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { Avatar } from '../components/ui/Avatar';
import { Badge } from '../components/ui/Badge';
import { CollapsibleCard, SectionBadge } from '../components/ui/CollapsibleCard';
import { ETAPA_MAP } from '../lib/constants';
import { DetalhesCompactos } from '../components/demandas/DetalhesCompactos';
import { ResultadosSection } from '../components/demandas/ResultadosSection';
import { ParceirosSection } from '../components/demandas/ParceirosSection';
import { PedidosInfoSection } from '../components/demandas/PedidosInfoSection';
import { EditarDatasAcao } from '../components/demandas/EditarDatasAcao';
import { AnexosSection } from '../components/demandas/AnexosSection';

const ETAPA_ORDER = ['nova_demanda', 'aprovada', 'em_captacao', 'em_edicao', 'aguardando_aprovacao', 'pronta_para_publicar', 'publicado'];

export function DemandaDetailSecretario() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [novaMsg, setNovaMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [counts, setCounts] = useState({ pedidosAbertos: 0, parceiros: 0, resultados: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/demandas/${id}`);
      setData(res);
      api.post(`/demandas/${id}/mensagens/lidas`).catch(() => {});
      Promise.all([
        api.get(`/demandas/${id}/pedidos-info`).catch(() => ({ pedidos: [] })),
        api.get(`/demandas/${id}/parceiros`).catch(() => ({ parceiros: [] })),
      ]).then(([p, pa]) => {
        setCounts({
          pedidosAbertos: p.pedidos.filter((x) => !x.resolvido_em && !x.cancelado_em).length,
          parceiros: pa.parceiros.length,
          resultados: res.demanda.resultados?.length || 0,
        });
      });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const enviar = async (e) => {
    e.preventDefault();
    if (!novaMsg.trim()) return;
    setSending(true);
    try {
      await api.post(`/demandas/${id}/mensagens`, { texto: novaMsg, tipo: 'resposta' });
      setNovaMsg('');
      await load();
    } finally { setSending(false); }
  };

  if (loading) return <div className="loader-full"><span className="spinner" /></div>;
  if (!data) return <div className="page">Demanda não encontrada.</div>;
  const { demanda, mensagens, anexos = [] } = data;

  const idxAtual = ETAPA_ORDER.indexOf(demanda.etapa);
  const cancelada = demanda.etapa === 'cancelada';

  const statusMsg = cancelada ? 'Esta demanda foi cancelada.' :
    demanda.etapa === 'publicado' ? '✅ Demanda publicada!' :
    demanda.etapa === 'aguardando_solicitante' ? '⚠️ A Comunicação precisa de informações suas. Role para responder.' :
    demanda.etapa === 'aguardando_aprovacao' ? 'Aguardando sua aprovação — você receberá um link por WhatsApp.' :
    demanda.etapa === 'pronta_para_publicar' ? '✅ Aprovado! A Comunicação vai publicar nos canais.' :
    'A Secretaria de Comunicação está cuidando. Acompanhe por aqui e pelo WhatsApp.';

  const mostrarResultados = counts.resultados > 0 || demanda.etapa === 'publicado';

  return (
    <div className="page">
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <button className="btn btn-ghost" onClick={() => nav(-1)}>
          <ArrowLeft size={15} /> Voltar
        </button>
        <button className="btn btn-ghost btn-sm" onClick={() => nav(`/demandas/${id}/timeline`)}>
          <History size={13} /> Histórico
        </button>
      </div>

      <div style={{ marginBottom: 14 }}>
        <div className="row-wrap" style={{ marginBottom: 4, gap: 6 }}>
          <Badge color={ETAPA_MAP[demanda.etapa]?.color}>{ETAPA_MAP[demanda.etapa]?.label}</Badge>
          {demanda.aprovacao === 'aprovado' && <Badge color="success">Aprovado</Badge>}
          {demanda.aprovacao === 'reprovado' && <Badge color="danger">Reprovado</Badge>}
          <span className="text-xs muted">#{demanda.id}</span>
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px', lineHeight: 1.25 }}>{demanda.titulo}</h1>
        <div className="text-xs muted">
          Solicitada em {format(new Date(demanda.created_at), 'dd/MM/yyyy')}
          {demanda.responsavel_nome && ` · responsável: ${demanda.responsavel_nome}`}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="funil-stepper">
            {ETAPA_ORDER.map((key, i) => {
              const status = cancelada ? (i <= 0 ? 'passou' : 'skip')
                : i < idxAtual ? 'passou' : i === idxAtual ? 'atual' : 'futuro';
              const label = ETAPA_MAP[key]?.label;
              return (
                <div key={key} className={`funil-step funil-step-${status}`}>
                  <div className="funil-step-dot" style={{
                    borderColor: status === 'passou' ? 'var(--c-success)' : status === 'atual' ? 'var(--c-primary)' : 'var(--c-border-strong)',
                    background: status === 'passou' ? 'var(--c-success)' : 'white',
                  }}>
                    {status === 'passou' ? <CheckCircle2 size={14} color="white" /> :
                      status === 'atual' ? <Clock size={14} color="var(--c-primary)" /> : null}
                  </div>
                  <div className="funil-step-label"><div className="funil-step-title">{label}</div></div>
                  {i < ETAPA_ORDER.length - 1 && (
                    <div className="funil-step-connector" style={{ background: status === 'passou' ? 'var(--c-success)' : 'var(--c-border)' }} />
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ padding: '10px 16px', background: 'var(--c-surface-2)', fontSize: 13 }}>
            {statusMsg}
          </div>
        </div>
      </div>

      {counts.pedidosAbertos > 0 && (
        <div className="card" style={{ marginBottom: 14, borderColor: 'var(--c-warning)', borderWidth: 2 }}>
          <div className="card-body" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10, background: 'var(--c-warning-soft)' }}>
            <AlertCircle size={18} color="var(--c-warning)" />
            <div style={{ flex: 1 }}>
              <strong style={{ fontSize: 13 }}>A equipe precisa de informações suas</strong>
              <div className="text-xs" style={{ color: '#78350f' }}>
                {counts.pedidosAbertos} pedido{counts.pedidosAbertos === 1 ? '' : 's'} aberto{counts.pedidosAbertos === 1 ? '' : 's'}. Role abaixo para responder.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid-2" style={{ alignItems: 'start', gap: 14 }}>
        <div className="stack" style={{ gap: 12 }}>
          <EditarDatasAcao demanda={demanda} onSalvo={load} />
          <DetalhesCompactos demanda={demanda} />

          <CollapsibleCard
            title="Imagens e arquivos"
            icon={ImageIcon}
            subtitle={anexos.length === 0 ? 'Anexe fotos, artes ou documentos de referência' : `${anexos.length} arquivo${anexos.length === 1 ? '' : 's'} anexado${anexos.length === 1 ? '' : 's'}`}
            badge={anexos.length > 0 && <SectionBadge color="info">{anexos.length}</SectionBadge>}
            defaultOpen={anexos.length > 0}
          >
            <AnexosSection
              demanda={demanda}
              anexos={anexos}
              podeEditar={true}
              onChange={(novos) => setData((d) => ({ ...d, anexos: novos }))}
            />
          </CollapsibleCard>

          <CollapsibleCard
            title="Pedidos de informação"
            icon={HelpCircle}
            subtitle={counts.pedidosAbertos > 0 ? 'Itens pendentes para você responder' : 'Nada pendente'}
            badge={counts.pedidosAbertos > 0 && <SectionBadge color="warning">{counts.pedidosAbertos}</SectionBadge>}
            alert={counts.pedidosAbertos > 0}
            defaultOpen={counts.pedidosAbertos > 0}
          >
            <PedidosInfoSection demanda={demanda} podeCriar={false} podeResponder={true} />
          </CollapsibleCard>

          <CollapsibleCard
            title="Parceiros e patrocinadores"
            icon={Users}
            subtitle={counts.parceiros === 0 ? 'Cadastre parceiros e envie link para subirem mídias' : `${counts.parceiros} cadastrado${counts.parceiros === 1 ? '' : 's'}`}
            badge={counts.parceiros > 0 && <SectionBadge color="purple">{counts.parceiros}</SectionBadge>}
            defaultOpen={counts.parceiros > 0}
          >
            <ParceirosSection demanda={demanda} podeEditar={true} />
          </CollapsibleCard>

          {mostrarResultados && (
            <CollapsibleCard
              title="Resultados publicados"
              icon={Link2}
              subtitle={counts.resultados === 0 ? 'Será preenchido pela Comunicação' : `${counts.resultados} link${counts.resultados === 1 ? '' : 's'}`}
              badge={counts.resultados > 0 && <SectionBadge color="success">{counts.resultados}</SectionBadge>}
              defaultOpen={demanda.etapa === 'publicado'}
            >
              <ResultadosSection demanda={demanda} podeEditar={false} onChange={(r) => setData((d) => ({ ...d, demanda: { ...d.demanda, resultados: r } }))} />
            </CollapsibleCard>
          )}
        </div>

        <div className="card">
          <div className="card-header" style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <MessageSquare size={15} color="var(--c-primary)" />
            <strong style={{ fontSize: 14 }}>Conversa com a Comunicação</strong>
            <span className="text-xs muted">· {mensagens.length}</span>
          </div>
          <div className="card-body" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="chat-list" style={{ maxHeight: 600, overflowY: 'auto' }}>
              {mensagens.length === 0 && <div className="text-sm muted center" style={{ padding: 20 }}>Sem mensagens ainda.</div>}
              {mensagens.map((m) => {
                const mine = m.autor_id === user?.id;
                const cls = `chat-msg ${mine ? 'mine' : ''} ${m.tipo === 'pergunta' ? 'pergunta' : ''}`;
                return (
                  <div key={m.id} className={cls}>
                    {!mine && <Avatar nome={m.autor_nome} src={m.autor_foto} size="sm" />}
                    <div className="chat-content">
                      <div className="chat-bubble">
                        {m.tipo === 'pergunta' && <div style={{ fontSize: 10, fontWeight: 600, opacity: 0.7, marginBottom: 2 }}>❓ PERGUNTA</div>}
                        {m.texto}
                      </div>
                      <div className="chat-meta">
                        {m.autor_nome?.split(' ')[0]} · {format(new Date(m.created_at), 'dd/MM HH:mm')}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={enviar} className="stack" style={{ gap: 6, borderTop: '1px solid var(--c-border)', paddingTop: 10 }}>
              <textarea className="textarea" placeholder="Escreva uma mensagem..." value={novaMsg} onChange={(e) => setNovaMsg(e.target.value)} rows={2} />
              <button type="submit" className="btn btn-primary btn-sm" disabled={sending || !novaMsg.trim()} style={{ alignSelf: 'flex-end' }}>
                <Send size={13} /> Enviar
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

